import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mail, MapPin, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create email content
    const emailSubject = `[Scratchflix] ${formData.subject}`;
    const emailBody = `
Nouveau message de: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Sujet: ${formData.subject}

Message:
${formData.message}

---
Envoyé depuis le formulaire de contact Scratchflix
    `.trim();
    
    // Create mailto link
    const mailtoLink = `mailto:scratchflix.nddc@ecole.be?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    toast({
      title: "Ouverture de votre client email...",
      description: "Envoyez l'email depuis votre client pour nous contacter.",
    });
    
    // Reset form after short delay
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="section-title">Contactez-Nous</h2>
        <p className="text-center text-lg text-[#6C757D] mb-12 max-w-3xl mx-auto">
          Tu as des questions sur Scratch ? Tu veux en savoir plus sur notre projet ? N'hésite pas à nous contacter ! Nous serons ravis de t'aider.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">Envoie-nous un message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        placeholder="Ton prénom"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        placeholder="Ton nom"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="ton.email@exemple.be"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Sujet *</Label>
                    <Select
                      value={formData.subject}
                      onValueChange={(value) => setFormData({ ...formData, subject: value })}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisis un sujet" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tutoriels">Question sur les tutoriels</SelectItem>
                        <SelectItem value="projet">Question sur un projet</SelectItem>
                        <SelectItem value="suggestion">Suggestion d'amélioration</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Écris ton message ici..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white font-semibold py-6 text-lg">
                    Envoyer le message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="text-[#FF6B35] mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#2C3E50]">Email</p>
                    <p className="text-sm text-[#6C757D]">scratchflix.nddc@ecole.be</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="text-[#0FBD8C] mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#2C3E50]">École</p>
                    <p className="text-sm text-[#6C757D]">Notre-Dame des Champs</p>
                    <p className="text-sm text-[#6C757D]">Uccle, Belgique</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="text-[#9966FF] mt-1" size={24} />
                  <div>
                    <p className="font-semibold text-[#2C3E50]">Réponse</p>
                    <p className="text-sm text-[#6C757D]">Nous répondons généralement en 48 heures !</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg bg-gradient-to-br from-[#FF6B35]/5 to-[#9966FF]/5 border-2 border-[#FF6B35]/20">
              <CardContent className="pt-6">
                <p className="text-center text-sm text-[#2C3E50] font-medium">
                  🎓 Projet d'entreprise scolaire - École Notre-Dame des Champs
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;